import React, { Component } from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from "react-redux";
import { setImage, setData } from '../../../redux/actionCreators';
import Webcam from "react-webcam";
import FooterContainer from '../../../components/footer/FooterContainer';
import MainButton from '../../../components/buttons/main_button/MainButton';
import { FaExclamationCircle } from "react-icons/fa";
import {
  GestureRecognizer,
  FilesetResolver
} from "@mediapipe/tasks-vision";
import { validateImageQuality } from '@autofact-lib/autofact-lib-check-image-quality';
import config from '../../../config/config';
import '../thumbFingerprintCapture.css';

class MediaPipeThumbFingerprintCapture extends Component {
  constructor(props) {
    super(props);

    // Constantes de validación
    this.AUTOCAPTURE_GESTURE = "Open_Palm";
    this.AUTOCAPTURE_CONFIDENCE = this.props.stepInfo.autoCaptureConfidence || 0.7; // Confianza para capturar automáticamente
    this.INSIDE_FRAME_MARGIN = this.props.stepInfo.insideFrameMargin || 0.02; // Margen de seguridad para considerar la mano dentro del frame (2%)
    this.CENTER_TOLERANCE = this.props.stepInfo.centerTolerance || 0.10; // Tolerancia para considerar la mano centrada (10%) en la pantalla
    this.VIDEO_READY_STATE_HAVE_CURRENT_DATA = 2;
    this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA = 4;
    this.HAND_LANDMARKS = 21;
    this.RECOGNIZER_MODE_IMAGE = 'IMAGE';
    this.RECOGNIZER_MODE_VIDEO = 'VIDEO';
    this.FEEDBACK_UPDATE_DURATION = 1200;
    this.CAMERA_FACING_MODES = {
      USER: 'user',
      ENVIRONMENT: 'environment'
    };
    this.state = {
      facingMode: props.stepInfo.defaultCamera || this.CAMERA_FACING_MODES.ENVIRONMENT,
      capturedImage: null,
      feedbackMessage: this.props.stepInfo.feedbackMessage || 'Coloca tu mano en el área de la cámara y se capturará automáticamente',
      iconColor: 'white',
      isGestureRecognizerLoaded: false,
      debugMode: ['true', true, "TRUE"].includes(config.debugMode),
    };


    this.validateImageQuality = props.stepInfo.validateImageQuality ?? true;
    this.isAppleDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    // Refs
    this.webcamRef = React.createRef();
    this.gestureRecognizerRef = React.createRef();
    this.lastVideoTimeRef = React.createRef();
    this.resultsRef = React.createRef();
    this.animationFrameRef = React.createRef();
    this.runningModeRef = React.createRef();
    this.autoCaptureEnabledRef = React.createRef();
    this.isCapturingRef = React.createRef();
    this.isAutoCaptureRef = React.createRef();
    this.retryCooldownRef = React.createRef();
    this.retryCooldownTimeoutRef = React.createRef();
    this.lastFeedbackUpdateRef = React.createRef();

    // Inicializar refs
    this.lastVideoTimeRef.current = -1;
    this.resultsRef.current = undefined;
    this.animationFrameRef.current = null;
    this.runningModeRef.current = this.RECOGNIZER_MODE_VIDEO;
    this.gestureRecognizerRef.current = null;
    this.autoCaptureEnabledRef.current = props.stepInfo.isAutoCapture ?? true;
    this.isCapturingRef.current = false;
    this.isAutoCaptureRef.current = false;
    this.retryCooldownRef.current = false;
    this.retryCooldownTimeoutRef.current = null;
    this.lastFeedbackUpdateRef.current = 0;

    // Bind methods
    this.captureImage = this.captureImage.bind(this);
    this.retry = this.retry.bind(this);
    this.setImageInternal = this.setImageInternal.bind(this);
    this.predictWebcam = this.predictWebcam.bind(this);
    this.isLandmarkInFrame = this.isLandmarkInFrame.bind(this);
    this.analyzeLandmarkVisibility = this.analyzeLandmarkVisibility.bind(this);
    this.isHandCentered = this.isHandCentered.bind(this);
    this.handleVideoLoaded = this.handleVideoLoaded.bind(this);
    this.toggleCamera = this.toggleCamera.bind(this);
    this.checkCapturedImageLandmarks = this.checkCapturedImageLandmarks.bind(this);
    this.allLandmarksVisible = this.allLandmarksVisible.bind(this);
    this.restartImageCapture = this.restartImageCapture.bind(this);
  }

  writeLog = (debugMode, ...log) => {
    if (debugMode) {
        console.log(...log);
    }
  };

  // Helper function to check if a landmark is within the frame
  isLandmarkInFrame(landmark) {
    if (!landmark || typeof landmark.x !== 'number' || typeof landmark.y !== 'number') {
      return false;
    }

    // Check if the landmark is within the frame with a safety margin
    const isXInFrame = this.INSIDE_FRAME_MARGIN < landmark.x && landmark.x < (1 - this.INSIDE_FRAME_MARGIN);
    const isYInFrame = this.INSIDE_FRAME_MARGIN < landmark.y && landmark.y < (1 - this.INSIDE_FRAME_MARGIN);

    return isXInFrame && isYInFrame;
  }

  allLandmarksVisible(visibilityInfo){
    return visibilityInfo !== null && visibilityInfo.total === this.HAND_LANDMARKS && visibilityInfo.visible === this.HAND_LANDMARKS;
  }

  // Helper function to analyze landmark visibility
  analyzeLandmarkVisibility(landmarks) {
    if (!landmarks || landmarks.length === 0) return null;

    const visibleLandmarks = landmarks.filter((landmark) =>
      this.isLandmarkInFrame(landmark)
    );

    return {
      total: landmarks.length,
      visible: visibleLandmarks.length,
      hidden: landmarks.length - visibleLandmarks.length,
      visibilityRate: (visibleLandmarks.length / landmarks.length * 100).toFixed(1),
    };
  }

  /**
   * Runs hand landmark detection on a single captured image (IMAGE mode).
   * Returns true only if all hand landmarks are visible in frame.
   * Always restores recognizer to VIDEO mode
   */
  async checkCapturedImageLandmarks(img) {
    const recognizer = this.gestureRecognizerRef.current;
    if (!recognizer) {
      return false;
    }
    try {
      await recognizer.setOptions({ runningMode: this.RECOGNIZER_MODE_IMAGE });
      const imgAnalysis = recognizer.recognize(img);
      if (!imgAnalysis?.landmarks?.length) {
        this.writeLog(this.state.debugMode, "No landmarks found in image");
        return false;
      }
      const visibilityInfo = this.analyzeLandmarkVisibility(imgAnalysis.landmarks[0]);
      this.writeLog(this.state.debugMode, "Landmarks visibility info", visibilityInfo);
      return this.allLandmarksVisible(visibilityInfo);
    } catch (e) {
      this.writeLog(this.state.debugMode, "Landmarks analysis error", e);
      return false;
    } finally {
      await recognizer.setOptions({ runningMode: this.RECOGNIZER_MODE_VIDEO });
    }
  }

  // Helper function to check if hand is centered
  isHandCentered(landmarks) {
    if (!landmarks || landmarks.length === 0) return false;

    // Calculate the centroid of the hand (average of all x and y coordinates)
    let sumX = 0;
    let sumY = 0;
    let validLandmarks = 0;

    for (const landmark of landmarks) {
      if (typeof landmark.x === 'number' && typeof landmark.y === 'number') {
        sumX += landmark.x;
        sumY += landmark.y;
        validLandmarks++;
      }
    }

    if (validLandmarks === 0) return false;

    const centerX = sumX / validLandmarks;
    const centerY = sumY / validLandmarks;

    // The center of the screen in normalized coordinates is (0.5, 0.5)
    // Check if the centroid is within the tolerance range
    const isXCentered = Math.abs(centerX - 0.5) <= this.CENTER_TOLERANCE;
    const isYCentered = Math.abs(centerY - 0.5) <= this.CENTER_TOLERANCE;

    return isXCentered && isYCentered;
  }

  restartImageCapture() {
    this.isCapturingRef.current = false;
    this.isAutoCaptureRef.current = false;
    const videoElement = this.webcamRef.current?.video;
    if (videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA && !this.state.capturedImage) {
      this.predictWebcam();
    }
  }

  async componentDidMount() {
    window.scrollTo(0, document.body.scrollHeight);
    await this.createGestureRecognizer();
    await this.getMediaDevices();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isGestureRecognizerLoaded && this.state.isGestureRecognizerLoaded) {
      const videoElement = this.webcamRef.current?.video;
      if (videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA && !this.state.capturedImage) {
        this.predictWebcam();
      }
    }
  }

  // Initialize Gesture Recognizer
  async createGestureRecognizer() {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        "/mediapipe/wasm"
      );
      const recognizer = await GestureRecognizer.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: '/mediapipe/models/gesture_recognizer.task',
          delegate: 'GPU'
        },
        runningMode: this.RECOGNIZER_MODE_VIDEO,
      });
      this.gestureRecognizerRef.current = recognizer;
      if (!this.state.isGestureRecognizerLoaded) {
        this.setState({ isGestureRecognizerLoaded: true });
      }
    } catch (error) {
      this.updateFeedback('Ocurrió un error, intente nuevamente.', 'red');
    }
  }

  componentWillUnmount() {
    if (this.animationFrameRef.current !== null) {
      cancelAnimationFrame(this.animationFrameRef.current);
      this.animationFrameRef.current = null;
    }
    if (this.retryCooldownTimeoutRef.current !== null) {
      clearTimeout(this.retryCooldownTimeoutRef.current);
      this.retryCooldownTimeoutRef.current = null;
    }
    if (this.gestureRecognizerRef.current) {
      this.gestureRecognizerRef.current.close();
      this.gestureRecognizerRef.current = null;
    }
    if (this.webcamRef.current) {
      const videoElement = this.webcamRef.current.video;
      if (videoElement && this.handleVideoLoaded) {
        videoElement.removeEventListener("loadeddata", this.handleVideoLoaded);
      }
      this.stopCameraStream();
    }
  }

  handleVideoLoaded() {
    if (this.state.isGestureRecognizerLoaded && this.gestureRecognizerRef.current && !this.state.capturedImage) {
      this.predictWebcam();
    }
  }

  async getMediaDevices() {
    try {
      if (this.animationFrameRef.current !== null) {
        cancelAnimationFrame(this.animationFrameRef.current);
        this.animationFrameRef.current = null;
      }

      const videoConstraints = {
        facingMode: this.state.facingMode,
        aspectRatio: 1,
        width: { ideal: 1920 },
        height: { ideal: 1920 }
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints
      });
      if (this.webcamRef.current) {
        const videoElement = this.webcamRef.current.video;
        if (videoElement) {
          videoElement.removeEventListener("loadeddata", this.handleVideoLoaded);

          videoElement.srcObject = stream;

          videoElement.addEventListener("loadeddata", this.handleVideoLoaded);

          // Si el video ya está listo, iniciar procesamiento inmediatamente
          if (videoElement.readyState >= this.VIDEO_READY_STATE_HAVE_CURRENT_DATA && this.state.isGestureRecognizerLoaded && this.gestureRecognizerRef.current && !this.state.capturedImage) {
            this.predictWebcam();
          }
        }
      }
    } catch (error) {
      this.updateFeedback('Error al acceder a la cámara. Por favor, verifica los permisos.', 'red');
    }
  }

  // Process webcam frames with MediaPipe
  async predictWebcam() {
    if (this.state.capturedImage) {
      return;
    }

    const videoElement = this.webcamRef.current?.video;
    const recognizer = this.gestureRecognizerRef.current;

    if (!videoElement || !recognizer || videoElement.readyState !== this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA) {
      return;
    }

    if (this.runningModeRef.current === this.RECOGNIZER_MODE_IMAGE) {
      this.runningModeRef.current = this.RECOGNIZER_MODE_VIDEO;
      await recognizer.setOptions({ runningMode: this.RECOGNIZER_MODE_VIDEO });
    }

    let nowInMs = Date.now();
    if (videoElement.currentTime !== this.lastVideoTimeRef.current) {
      this.lastVideoTimeRef.current = videoElement.currentTime;
      this.resultsRef.current = recognizer.recognizeForVideo(videoElement, nowInMs);
    }

    // No process if we are in cooldown after retry
    if (this.retryCooldownRef.current) {
      if (!this.state.capturedImage && videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA) {
        this.animationFrameRef.current = requestAnimationFrame(() => this.predictWebcam());
      }
      return;
    }

    if (this.resultsRef.current?.gestures && this.resultsRef.current.gestures.length > 0) {
      const categoryName = this.resultsRef.current.gestures[0][0].categoryName;
      const categoryScore = parseFloat(
        (this.resultsRef.current.gestures[0][0].score).toString()
      ).toFixed(2);
      const confidence = parseFloat(categoryScore);
      let allLandmarksVisible = false;
      let handIsCentered = false;

      if (this.resultsRef.current.landmarks && this.resultsRef.current.landmarks.length > 0) {
        const visibilityInfo = this.analyzeLandmarkVisibility(this.resultsRef.current.landmarks[0]);
        allLandmarksVisible = this.allLandmarksVisible(visibilityInfo);
        handIsCentered = this.isHandCentered(this.resultsRef.current.landmarks[0]);
      }

      if (categoryName === this.AUTOCAPTURE_GESTURE) {
        if (!handIsCentered) {
          this.updateFeedback('Centra tu mano en la pantalla', 'white');
        } else if (!allLandmarksVisible) {
          this.updateFeedback('Asegúrate de que toda la mano sea visible', 'white');
        } else if (confidence < this.AUTOCAPTURE_CONFIDENCE) {
          this.updateFeedback('Mantén la palma abierta', 'white');
        } else {
          // Auto-capture when all conditions are met
          // All validations passed (handIsCentered, allLandmarksVisible, confidence >= threshold)
          // No capture if we are in cooldown period after retry
          if (this.autoCaptureEnabledRef.current &&
            !this.isCapturingRef.current &&
            !this.retryCooldownRef.current) {
            this.isCapturingRef.current = true;
            this.isAutoCaptureRef.current = true; // Marcar como captura automática
            setTimeout(() => {
              if (!this.state.capturedImage) {
                this.captureImage();
              }
              this.isCapturingRef.current = false;
            }, 800); // Small delay to ensure good capture
          }
        }
      } else {
        this.updateFeedback('Abre la palma de tu mano', 'white');
      }
    } else {
      // No hand detected
      const initialMessage = this.props.stepInfo.feedbackMessage ||
        'Coloca tu mano en el centro de la cámara';
      this.updateFeedback(initialMessage, 'white');
    }

    if (!this.state.capturedImage && videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA) {
      this.animationFrameRef.current = requestAnimationFrame(() => this.predictWebcam());
    }
  }

  updateFeedback(message, color, isPriority = false) {
    const now = Date.now();
    const timeSinceLastUpdate = now - this.lastFeedbackUpdateRef.current;

    if (!isPriority && timeSinceLastUpdate < this.FEEDBACK_UPDATE_DURATION) {
      return;
    }

    if (this.state.feedbackMessage !== message || this.state.iconColor !== color) {
      this.lastFeedbackUpdateRef.current = now;
      this.setState({
        feedbackMessage: message,
        iconColor: color,
      });
    }
  }

  stopCameraStream() {
    const videoElement = this.webcamRef.current?.video;
    if (videoElement && videoElement.srcObject) {
      const stream = videoElement.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoElement.srcObject = null;
    }
  }

  finalizeCapture(imageSrc, data, isAutoCapture) {
    this.setImageInternal(imageSrc);
    this.props.setData(data);
    this.stopCameraStream();
    if (this.gestureRecognizerRef.current) {
      this.gestureRecognizerRef.current.close();
      this.gestureRecognizerRef.current = null;
    }
    if (isAutoCapture) {
      this.isAutoCaptureRef.current = false; // Resetear flag después de usar
    }
    this.setState({ capturedImage: imageSrc });
  }

  async checkCapturedImageQuality(img) {
    const qualityResult = await validateImageQuality(
      img,
      { customThresholds: config.fingerprintCustomThresholds, debugMode: this.state.debugMode },
      'front'
    );

    let response = { isValid: false, result: qualityResult };
    let feedbackIsPriority = true;

    // Check if the brightness is valid (low light)
    if (!qualityResult.brightness) {
      this.updateFeedback('Hay poca luz. Por favor, aumenta la iluminación', 'red', feedbackIsPriority);
      return response;
    }

    // Check if the contrast is valid (too much light)
    if (!qualityResult.contrast) {
      this.updateFeedback('Hay demasiada luz. Redúcela para equilibrar el color y contraste', 'red', feedbackIsPriority);
      return response;
    }

    // Check if the sharpness is valid (blurry)
    if (!qualityResult.sharpness) {
      this.updateFeedback('La imagen está borrosa. Ajusta la distancia para enfocar mejor', 'yellow', feedbackIsPriority);
      return response;
    }
    response.isValid = true;
    return response;
  }

  createCaptureData(qualityResult = null) {
    const baseData = {
      provider: 'mediapipe',
      timestamp: new Date().toISOString(),
      validateImageQuality: this.validateImageQuality,
      isAppleDevice: this.isAppleDevice,
    };

    if (qualityResult) {
      return { ...baseData, ...qualityResult };
    }

    return baseData;
  }

  captureImage() {
    if (this.animationFrameRef.current !== null) {
      cancelAnimationFrame(this.animationFrameRef.current);
      this.animationFrameRef.current = null;
    }

    const isAutoCapture = this.isAutoCaptureRef.current;

    const videoElement = this.webcamRef.current?.video;
    if (videoElement && videoElement.readyState >= this.VIDEO_READY_STATE_HAVE_CURRENT_DATA) {
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      const imageSrc = canvas.toDataURL('image/jpeg', 1.0);
      const img = new Image();
      img.src = imageSrc;

      img.onload = async () => {
        let feedbackIsPriority = true;

        const allLandmarksCaptured = await this.checkCapturedImageLandmarks(img);
        if (!allLandmarksCaptured) {
          this.updateFeedback('La mano completa debe estar visible', 'red', feedbackIsPriority);
          this.writeLog(this.state.debugMode, 'Hand not completely visible, restarting capture');
          this.restartImageCapture();
          return;
        }

        let data;
        if (this.validateImageQuality) {
          const { isValid, qualityResult } = await this.checkCapturedImageQuality(img);
          this.writeLog(this.state.debugMode, 'Image quality check - ', 'is valid: ', isValid, 'result: ', qualityResult);

          if (!isValid) {
            this.writeLog(this.state.debugMode, 'invalid quality, restarting capture');
            this.restartImageCapture();
            return;
          }
          this.updateFeedback('¡Perfecto! Capturando...', 'green');
          data = this.createCaptureData(qualityResult);
        } else {
          data = this.createCaptureData();
        }
        
        this.finalizeCapture(imageSrc, data, isAutoCapture);
      };

      img.onerror = () => {
        // If there is an error, proceed with the capture without validation
        const data = this.createCaptureData();
        this.finalizeCapture(imageSrc, data, isAutoCapture);
      };
    }
  }

  setImageInternal = (image) => {
    try {
      this.props.setImage(image);
    } catch (error) {
      console.error("Error al guardar la imagen:", error);
    }
  }

  async retry() {
    if (this.retryCooldownTimeoutRef.current !== null) {
      clearTimeout(this.retryCooldownTimeoutRef.current);
      this.retryCooldownTimeoutRef.current = null;
    }

    if (this.animationFrameRef.current !== null) {
      cancelAnimationFrame(this.animationFrameRef.current);
      this.animationFrameRef.current = null;
    }

    this.isCapturingRef.current = false;
    this.isAutoCaptureRef.current = false;

    this.resultsRef.current = undefined;
    this.lastVideoTimeRef.current = -1;

    this.retryCooldownRef.current = true;

    const initialMessage = this.props.stepInfo.feedbackMessage ||
      'Coloca tu mano en el área de la cámara y presiona el botón para capturar';

    const needsUpdate = this.state.capturedImage !== null ||
      this.state.feedbackMessage !== initialMessage ||
      this.state.iconColor !== 'white';

    const executeRetry = async () => {
      await this.createGestureRecognizer();
      await this.getMediaDevices();
      this.retryCooldownTimeoutRef.current = setTimeout(() => {
        this.retryCooldownRef.current = false;
        this.retryCooldownTimeoutRef.current = null;
      }, 3000);
    };

    if (needsUpdate) {
      this.lastFeedbackUpdateRef.current = Date.now();
      this.setState({
        capturedImage: null,
        feedbackMessage: initialMessage,
        iconColor: 'white'
      }, executeRetry);
    } else {
      executeRetry();
    }
  }

  async toggleCamera() {
    // Detener el stream actual
    if (this.webcamRef.current?.video?.srcObject) {
      const stream = this.webcamRef.current.video.srcObject;
      stream.getTracks().forEach(track => track.stop());
    }

    // Cambiar el facingMode
    this.setState(prevState => ({
      facingMode: prevState.facingMode === this.CAMERA_FACING_MODES.USER ? this.CAMERA_FACING_MODES.ENVIRONMENT : this.CAMERA_FACING_MODES.USER
    }), async () => {
      // Reiniciar el stream con la nueva cámara
      await this.getMediaDevices();
    });
  }

  render() {
    const videoConstraints = {
      facingMode: { ideal: this.state.facingMode },
      aspectRatio: 1,
      autoGainControl: true,
      audio: false,
      width: { ideal: 1920 },
      height: { ideal: 1920 }
    };
    const ToggleCameraIcon = require('../../../resources/images/turncamera.svg').default;

    return (
      <>
        {this.state.capturedImage ? (
          <div className={'preview thumb-preview'}>
            <Grid>
              <div className="content">
                <Row>
                  <Col xs={12} className={'content-image'}>
                    <p className={'content-description'}>
                      Asegúrate de que la captura cumpla con lo siguiente para evitar rechazos:
                    </p>
                    <ul>
                      <li>Es tu palma (no el dorso)</li>
                      <li>La imagen es nítida y enfocada</li>
                      <li>La palma está completa dentro del recuadro</li>
                    </ul>
                    <img src={this.state.capturedImage} alt="Huella digital capturada" />
                  </Col>
                </Row>
                <FooterContainer>
                  <Col xs={6}>
                    <div className={'camera-button'}>
                      <MainButton
                        onClick={this.retry}
                        text={'Repetir'}
                        outline={true}
                      />
                    </div>
                  </Col>
                  <Col xs={6}>
                    <MainButton
                      onClick={this.props.nextStep}
                      className={'next-button'}
                    >
                      {this.props.stepInfo.buttonText || 'Continuar'}
                    </MainButton>
                  </Col>
                </FooterContainer>
              </div>
            </Grid>
          </div>
        ) : (
          <div className={'thumb-fingerprint-capture'}>
            <Webcam
              audio={false}
              ref={this.webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className={'thumb-webcam'}
              playsInline
            />
            <div className={`thumb-feedback-message thumb-feedback iconColor-${this.state.iconColor}`}>
              <FaExclamationCircle className="thumb-feedback-icon" />
              <span className="thumb-feedback-text">{this.state.feedbackMessage}</span>
            </div>
            <div className="button-container-thumb">
              {!this.autoCaptureEnabledRef.current && (
                <Button
                  className={'capture-button thumb-capture-btn'}
                  onClick={this.captureImage}
                />
              )}
              <div className={'toggle-camera-button'} onClick={this.toggleCamera}>
                <img src={ToggleCameraIcon} alt="Toggle camera" />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    stepInfo: state.stepInfo
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setImage(image) {
      dispatch(setImage(image));
    },
    setData: (data) => {
      dispatch(setData(data));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MediaPipeThumbFingerprintCapture);
